import 'whatwg-fetch'
import * as JspUtil from '../common/JspUtil'
import { hashHistory } from './navigationService';
import * as constants from '../common/constants'
import { getInitialRoute, jspGlobals } from './jspGlobals'
import store from '../store'

export const JSON_CONTENT_TYPE = "application/json; charset=UTF-8";

// Global error handler — set by React (GlobalErrorHandler component) to show errors via MUI
let _globalErrorHandler = null;
export function setGlobalErrorHandler(handler) { _globalErrorHandler = handler; }
function showGlobalError(message) {
    if (_globalErrorHandler) _globalErrorHandler(message);
    else console.error(message);
}

let _cacheBuster = Math.floor(new Date().getTime() / 1000)

const _ajaxCall = (url, cb, specs, timeoutMiliSeconds, noparsing) => {
    // SSO AuthFilter expects all API (aka AJAX) calls to have this header. When it sees this header, it will issue a
    // 401 on an invalid session. Otherwise, it assumes the call is not a AJAX call and will issue a 302 instead.
    specs.headers = { ...specs.headers, 'X-Requested-With': 'XMLHttpRequest' }

    // Federated dashboard APIs require the bearer token supplied by the login servlet. Preserve it in session
    // storage so subsequent React routes can continue using it after the JSP bootstrap global is no longer present.
    if (url.includes('/logoutUserToken') || url.includes('/logoutUserSessions')) {
        sessionStorage.removeItem('imtAccessToken')
        sessionStorage.removeItem('imtUserName')
    }
    let accessToken = jspGlobals.jspAccessToken
    if (accessToken) {
        sessionStorage.setItem('imtAccessToken', accessToken)
    }
    else {
        accessToken = sessionStorage.getItem('imtAccessToken')
    }
    if (accessToken) {
        specs.headers = { ...specs.headers, authorization: `Bearer ${accessToken}` }
    }

    async function fetchWithTimeout(resource, specs, timeoutMiliSeconds) {
        if (timeoutMiliSeconds) {
            let controller = null;
            try {
                controller = new AbortController();
            } catch (error) {                
            }
            if (controller) {
                const id = setTimeout(() => controller.abort(), timeoutMiliSeconds);
                const response = await fetch(resource, {
                    ...specs,
                    signal: controller.signal
                });
                clearTimeout(id);
                return response;
            }
            else {
                return Promise.race([
                    fetch(resource, specs),
                    new Promise((_, reject) => setTimeout(
                        () => reject(new Error('timeout')), timeoutMiliSeconds)),
                ]);
            }
        }
        else {
            return fetch(url, specs);
        }
    }

    function handleInvalidSession() {
        const msg = 'Invalid session. You will be redirected to login page.';
        showGlobalError(msg);
        // Skip redirect when the server has directed us to a jspPage-driven flow
        // that legitimately runs without an auth session.
        if (getInitialRoute()) return;
        setTimeout(() => {
            hashHistory.push(constants.ROUTE_PAGE_LOGIN);
        }, 1000)
    }

    if (specs && specs.method === 'GET') {
        // model the cache buster query param "_" after JQuery
        url = url + (url.includes("?") ? "&" : "?") + "_=" + _cacheBuster++;
    }

    return fetchWithTimeout(url, specs, timeoutMiliSeconds, noparsing)
        .then((response) => {
            if (response.ok) {
                if (noparsing) {
                    return Promise.resolve({ "result": { "data": "", "message": "OK", "code": "0" } });
                }
                return Promise.resolve(response.json())
            }
            else {
                let error = new Error(response.status + " " + response.statusText)
                error.response = response
                return Promise.reject(error)
            }
        })
        .then((response) => {
            try {
                let resp = response;
                if (response.error && response.error.code) {
                    resp = response.error;
                }
                else if (response.result && response.result.code) {
                    resp = response.result;
                }
                cb(resp);
            } catch (error) { //to avoid calling cb again in common error handling below
                console.error('action=_ajaxCall step=Callback-happypath-failed error=' + error)
                alert('ajax call failed with error:' + error)
            }
        })
        .catch((error) => {

            console.error(error)

            if (error.message.startsWith("401")) {
                handleInvalidSession()
                cb.handlesOwnError && cb({ error, url })
            }
            else if (error.message.startsWith("403")) {
                showGlobalError('You are not authorized to perform this operation. Please re-login and try again.');
                cb.handlesOwnError && cb({ error, url })
            }
            else if (cb.handlesOwnError) {
                cb({ error, url })
            }
            else {
                let msg = 'Service call failed: [' + error.message + ']';
                try {
                    error.response.json().then(json => {
                        msg += ', message: ' + json.error + ', details: ' + json.details;
                        showGlobalError(msg);
                    });
                } catch (err) {
                    showGlobalError(msg);
                }
            }
        })
}

export const formPost = (url, data, cb, withHeaders) => {
    return _ajaxCall(url, cb, {
        method: 'POST',
        credentials: 'include',
        headers: (typeof (data) === "string") ?
            {
                'Content-Type': 'application/x-www-form-urlencoded', ...withHeaders
            } : { ...withHeaders },
        body: data
    })
}

export const apiGet = (url, hdrs, cb) => {
    return _ajaxCall(url, cb, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'ICEApp': 'SSO React Client',
            'Content-Type': JSON_CONTENT_TYPE,
            ...hdrs
        }
    })
}

export const apiTimeoutGet = (url, hdrs, cb, timeoutMiliSeconds) => {
    return _ajaxCall(url, cb, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'ICEApp': 'SSO React Client',
            'Content-Type': JSON_CONTENT_TYPE,
            ...hdrs
        }
    }, timeoutMiliSeconds, true)
}

export const apiPost = (url, hdrs, data, cb, timeoutMiliSeconds) => {
    let csrfToken = '';
    if (typeof jspCSRFToken !== 'undefined' && JspUtil.isValueAvailable(jspCSRFToken)) {
        csrfToken = jspCSRFToken;
    }
    return _ajaxCall(url, cb, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'ICEApp': 'SSO React Client',
            'CSRFToken': csrfToken,
            'Content-Type': JSON_CONTENT_TYPE,
            ...hdrs
        },
        body: (typeof (data) === "string") ? data : JSON.stringify(data)
    },
        timeoutMiliSeconds)
}

export const generatePassword = (length) => {
    let randomNumber = '';
    let allowed = "0123456789";
    for (let i = 0; i < length; i++) {
        randomNumber += allowed.charAt(Math.floor(Math.random() * allowed.length));
    }
    return randomNumber;
}

const SESSION_EXPIRED_CODES = [-32002, -32003];

// Guards against multiple concurrent -32002/-32003 responses queueing N redirects
// and N AUTH_SESSION dispatches. Reset after the redirect fires (or is skipped).
let _redirectingToLogin = false;

export function apiPostAsync(url, headers, data, timeoutMiliSeconds) {
    return new Promise((resolve, reject) => {
        const cb = resp => {
            if (resp.error) {
                reject({ message: 'Service unavailable. Please try again.', code: -1 });
            } else if (resp.code != 0) {
                if (SESSION_EXPIRED_CODES.includes(Number(resp.code))) {
                    // Only redirect to login when the server hasn't directed us to a
                    // specific page via jspPage. Flows like CHANGE_PASSWORD / REGISTER_GID /
                    // CONFIRM_USER_EMAIL legitimately lack an auth session — the landing
                    // page handles that on its own and must not be bounced to /pageLogin.
                    if (!getInitialRoute() && !_redirectingToLogin) {
                        _redirectingToLogin = true;
                        // Clear stale auth so PageLogin's `if (authenticatedUser.user)`
                        // guard doesn't bounce us straight back to /pageAuthenticated.
                        store.dispatch({ type: 'AUTH_SESSION', data: { authenticatedUser: {}, isAuthenticated: false } });
                        setTimeout(() => {
                            // navigationService.push dedups same-path navigation, so this
                            // is a no-op when the failing API was triggered from /pageLogin.
                            hashHistory.push(constants.ROUTE_PAGE_LOGIN);
                            _redirectingToLogin = false;
                        }, 1500);
                    }
                }
                reject(resp);
            } else {
                resolve(resp);
            }
        };
        cb.handlesOwnError = true;
        apiPost(url, headers, data, cb, timeoutMiliSeconds);
    });
}

